import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";

import { AuthShell } from "../components/AuthShell";
import { IconoMorfo } from "../components/IconoMorfo";
import { LabeledField } from "../components/LabeledField";
import { authApi } from "../api/endpoints";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [values, setValues] = useState({ password: "", repeat: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(token ? null : "errors.resetLinkInvalid");
  const [done, setDone] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    if (values.password !== values.repeat) {
      setError("errors.passwordsDontMatch");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await authApi.resetPassword({ token, password: values.password });
      setDone(true);
    } catch (apiError) {
      setError(apiError.code ?? "errors.unexpected");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t("auth.heroTitle")}
      subtitle={t("auth.heroSubtitle")}
      trustPoints={[t("auth.trustDocuments"), t("auth.trustReview")]}
    >
      <Box sx={{ width: "100%", maxWidth: 410 }}>
        {done ? (
          <Stack spacing={2.5} sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "success.light",
                color: "success.main",
                mx: "auto",
              }}
            >
              <CheckIcon fontSize="large" />
            </Box>
            <Typography variant="h2">{t("auth.passwordUpdated")}</Typography>
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={() => navigate("/")}
            >
              {t("auth.signIn")}
            </Button>
          </Stack>
        ) : (
          <Box component="form" onSubmit={submit} noValidate>
            <Typography variant="h2">{t("auth.resetTitle")}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, mb: 3 }}
            >
              {t("auth.passwordHint")}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5 }}>
                {t(error, { defaultValue: t("errors.unexpected") })}
              </Alert>
            )}

            <Stack spacing={2}>
              <LabeledField
                label={t("auth.newPassword")}
                type={showPassword ? "text" : "password"}
                required
                autoComplete="new-password"
                value={values.password}
                onChange={(event) =>
                  setValues({ ...values, password: event.target.value })
                }
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label={t(
                            showPassword
                              ? "auth.hidePassword"
                              : "auth.showPassword",
                          )}
                          onClick={() => setShowPassword((value) => !value)}
                        >
                          {/* Un solo elemento al que le cambia el trazo: si
                              se cambiase el elemento entero, React montaría
                              un icono nuevo y se vería un salto, no un
                              morfeo. */}
                          <IconoMorfo
                            trazo={showPassword ? "ojoTachado" : "ojo"}
                            size={21}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <LabeledField
                label={t("auth.repeatPassword")}
                type={showPassword ? "text" : "password"}
                required
                autoComplete="new-password"
                value={values.repeat}
                onChange={(event) =>
                  setValues({ ...values, repeat: event.target.value })
                }
              />
            </Stack>

            <Button
              type="submit"
              fullWidth
              size="large"
              variant="contained"
              disabled={submitting || !token}
              sx={{ mt: 3 }}
            >
              {submitting ? (
                <>
                  <CircularProgress
                    size={18}
                    color="inherit"
                    sx={{ mr: 1.5 }}
                  />
                  {t("common.saving")}
                </>
              ) : (
                t("common.save")
              )}
            </Button>
          </Box>
        )}
      </Box>
    </AuthShell>
  );
}
